import { useEffect, useState } from "react";
import Seo from "@/components/Seo";
import api from "@/utils/api";

const AdminPromos = () => {
  const [token, setToken] = useState(localStorage.getItem("fastalu_admin_token") || "");
  const [promos, setPromos] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_date: "",
    end_date: "",
    is_active: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState("");

  const fetchPromos = async (currentToken) => {
    if (!currentToken) return;
    try {
      const response = await api.get("/api/promos?include_inactive=true", {
        headers: { "x-admin-token": currentToken },
      });
      setPromos(response.data);
      setStatus("");
    } catch (error) {
      setStatus("Accès refusé ou erreur API");
    }
  };

  useEffect(() => {
    fetchPromos(token);
  }, [token]);

  const handleChange = (field) => (event) => {
    const value = field === "is_active" ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!token || !formData.title) {
      setStatus("Renseignez le token admin et un titre.");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/api/promos/${editingId}`, formData, {
          headers: { "x-admin-token": token },
        });
      } else {
        await api.post("/api/promos", formData, {
          headers: { "x-admin-token": token },
        });
      }
      setFormData({ title: "", description: "", start_date: "", end_date: "", is_active: true });
      setEditingId(null);
      fetchPromos(token);
    } catch (error) {
      setStatus("Erreur lors de l’enregistrement.");
    }
  };

  const handleEdit = (promo) => {
    setEditingId(promo.id);
    setFormData({
      title: promo.title,
      description: promo.description,
      start_date: promo.start_date,
      end_date: promo.end_date,
      is_active: promo.is_active,
    });
  };

  const handleToggle = async (promo) => {
    try {
      await api.put(
        `/api/promos/${promo.id}`,
        { is_active: !promo.is_active },
        { headers: { "x-admin-token": token } },
      );
      fetchPromos(token);
    } catch (error) {
      setStatus("Erreur lors de la mise à jour.");
    }
  };

  const handleDelete = async (promoId) => {
    try {
      await api.delete(`/api/promos/${promoId}`, {
        headers: { "x-admin-token": token },
      });
      fetchPromos(token);
    } catch (error) {
      setStatus("Erreur lors de la suppression.");
    }
  };

  return (
    <div className="bg-white" data-testid="admin-promos-page">
      <Seo title="Admin Promotions | Fast Alu" description="Gestion des promotions Fast Alu." />
      <section className="bg-brand-blue/5" data-testid="admin-promos-hero">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <h1 className="font-heading text-3xl uppercase" data-testid="admin-promos-title">
            Back-office promotions
          </h1>
          <p className="text-brand-ink/70 mt-2" data-testid="admin-promos-subtitle">
            Créez, modifiez et activez vos offres.
          </p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-[1.1fr_1.5fr] gap-8">
        <div className="border border-brand-blue/15 rounded-sm p-6" data-testid="admin-promos-form">
          <label className="block text-sm uppercase font-semibold" data-testid="admin-token-label">
            Token admin
          </label>
          <input
            className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
            value={token}
            onChange={(event) => {
              setToken(event.target.value);
              localStorage.setItem("fastalu_admin_token", event.target.value);
            }}
            data-testid="admin-token-input"
          />
          <div className="mt-4 space-y-3">
            <input
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
              placeholder="Titre"
              value={formData.title}
              onChange={handleChange("title")}
              data-testid="promo-title-input"
            />
            <textarea
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2 min-h-[120px]"
              placeholder="Description"
              value={formData.description}
              onChange={handleChange("description")}
              data-testid="promo-desc-input"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                type="date"
                className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
                value={formData.start_date}
                onChange={handleChange("start_date")}
                data-testid="promo-start-input"
              />
              <input
                type="date"
                className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
                value={formData.end_date}
                onChange={handleChange("end_date")}
                data-testid="promo-end-input"
              />
            </div>
            <label className="flex items-center gap-2 text-xs" data-testid="promo-active-label">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={handleChange("is_active")}
                data-testid="promo-active-checkbox"
              />
              Promotion active
            </label>
            <button
              onClick={handleSave}
              className="w-full bg-brand-red text-white rounded-full py-2 text-xs uppercase font-semibold"
              data-testid="promo-save"
            >
              {editingId ? "Mettre à jour" : "Créer la promotion"}
            </button>
            {status && (
              <p className="text-xs text-brand-red" data-testid="promo-status">
                {status}
              </p>
            )}
          </div>
        </div>
        <div className="space-y-4" data-testid="admin-promos-list">
          {promos.map((promo) => (
            <div
              key={promo.id}
              className="border border-brand-blue/15 rounded-sm p-4 bg-white"
              data-testid={`admin-promo-card-${promo.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl uppercase" data-testid={`admin-promo-title-${promo.id}`}>
                    {promo.title}
                  </h2>
                  <p className="text-sm text-brand-ink/70" data-testid={`admin-promo-desc-${promo.id}`}>
                    {promo.description}
                  </p>
                  <p className="text-xs text-brand-blue mt-2" data-testid={`admin-promo-dates-${promo.id}`}>
                    {promo.start_date} {promo.end_date && `→ ${promo.end_date}`}
                  </p>
                </div>
                <span
                  className={`text-xs uppercase font-semibold ${promo.is_active ? "text-brand-red" : "text-brand-blue"}`}
                  data-testid={`admin-promo-status-${promo.id}`}
                >
                  {promo.is_active ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={() => handleEdit(promo)}
                  className="text-xs uppercase font-semibold text-brand-blue"
                  data-testid={`admin-promo-edit-${promo.id}`}
                >
                  Modifier
                </button>
                <button
                  onClick={() => handleToggle(promo)}
                  className="text-xs uppercase font-semibold text-brand-red"
                  data-testid={`admin-promo-toggle-${promo.id}`}
                >
                  {promo.is_active ? "Désactiver" : "Activer"}
                </button>
                <button
                  onClick={() => handleDelete(promo.id)}
                  className="text-xs uppercase font-semibold text-brand-red"
                  data-testid={`admin-promo-delete-${promo.id}`}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminPromos;
